import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.ts'
import { SequencingPlatforms } from '../../Generated/SequencingPlatform.ts'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.ts'
import { FormControl, Label } from '../Common/index.ts'
import { TooltipTrigger } from '../../Provider/TooltipManager.tsx'
import {
  getEffectiveSequencingPlatform,
  getSequencingPlatformInlineBadgeStyle,
  getSequencingPlatformLabel,
  getSequencingPlatformOverrideWarning,
  isProcessingTypeAllowedForPlatform,
  sequencingPlatformAutoDetectedText,
  sequencingPlatformSettingLabel,
  sequencingPlatformSettingTooltip
} from '../../Util/sequencingPlatform.ts'
import { SequencingPlatformEarlyAccessTag } from './SequencingPlatformEarlyAccessTag.tsx'

export interface SequencingPlatformSettingRowProps {
  detected?: SequencingPlatformKey | null
  override?: SequencingPlatformKey | null
  /** The processing lab before submit: picks or clears the override. Without it the row only shows the platform. */
  onOverride?: (platform: SequencingPlatformKey | null) => void
  processingType?: ProcessingTypeKey
  sampleCount?: number
  disabled?: boolean
}

export const SequencingPlatformSettingRow = ({
  detected,
  override,
  onOverride,
  processingType,
  sampleCount = 0,
  disabled = false
}: SequencingPlatformSettingRowProps) => {
  const platform = getEffectiveSequencingPlatform(detected, override)
  const isOverridden = !!override && override !== detected
  if (onOverride) {
    const warning = override ? getSequencingPlatformOverrideWarning(detected ?? null, override, sampleCount) : null
    return (
      <FormControl>
        <TooltipTrigger
          tooltip="Read from the FASTQ headers on upload. Re-exported reads (from SRA, for example) lose those headers: choose the platform here then. It can only be changed before the order is submitted."
          withIcon
        >
          <Label inline valid={true}>
            {sequencingPlatformSettingLabel}
          </Label>
        </TooltipTrigger>
        <select
          data-testid="sequencing-platform-setting-select"
          className="border border-gray-400 hover:border-gray-500 rounded-lg px-4 py-0.5 sm:py-1 mt-2 bg-font-contrast text-font-default focus:outline-primary outline-offset-2 outline-1 w-full"
          value={override ?? ''}
          disabled={disabled}
          onChange={(e) => onOverride(e.target.value === '' ? null : (e.target.value as SequencingPlatformKey))}
        >
          <option value="" disabled={!detected}>
            {detected ? `As detected: ${SequencingPlatforms[detected].label}` : 'Not detected: choose a platform…'}
          </option>
          {Object.values(SequencingPlatforms).map(({ key, label }) => (
            <option key={key} value={key} disabled={!!processingType && !isProcessingTypeAllowedForPlatform(processingType, key)}>
              {detected && key !== detected ? `Override to ${label}` : label}
            </option>
          ))}
        </select>
        {isOverridden && detected && (
          <p className="mt-1 text-sm text-warning" data-testid="sequencing-platform-setting-warning">
            The files say {SequencingPlatforms[detected].label}. If they are not {SequencingPlatforms[override].label} data, processing can fail
            or produce wrong results.{warning ? ` ${warning}` : ''}
          </p>
        )}
      </FormControl>
    )
  }

  return (
    <FormControl>
      <TooltipTrigger tooltip={sequencingPlatformSettingTooltip} withIcon>
        <Label inline valid={true}>
          {sequencingPlatformSettingLabel}
        </Label>
      </TooltipTrigger>
      <div className="mt-2 px-1 text-sm text-font-default" data-testid="sequencing-platform-setting">
        {platform ? (
          <span
            data-testid="sequencing-platform-setting-badge"
            className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold ${getSequencingPlatformInlineBadgeStyle(platform)}`}
          >
            {getSequencingPlatformLabel(platform)}
            {isOverridden ? ' (overridden)' : ''}
            <SequencingPlatformEarlyAccessTag platform={platform} />
          </span>
        ) : (
          <span className="text-base-content/70">{sequencingPlatformAutoDetectedText}</span>
        )}
      </div>
    </FormControl>
  )
}
